import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import { DynamicModule, InputsType } from 'ng-dynamic-component';
import { combineLatest, from, map, Observable, switchMap } from 'rxjs';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import { AlertType } from '@valk-nx/components/ui-alert/src/lib/contracts/alert.types';
import { SkeletonComponent } from '@valk-nx/components/ui-skeleton/src/lib/skeleton';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import {
	LANGUAGE,
	Language,
	PRICING_TOOLTIP_OCCUPANCY,
} from '@valk-nx/core/lib/core';
import { SectionBgClass } from '@valk-nx/core/lib/pipes/section-class/section-class.pipe';
import { CarouselComponent as SbCarouselComponent } from '@valk-nx/storyblok/components/carousel/src/lib/carousel';
import { SectionComponent } from '@valk-nx/storyblok/components/section/src/lib/section';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import {
	removeParameters,
	removeTrailingSlash,
} from '@valk-nx/storyblok-helpers/src/lib/general/sitemap.helper';
import { PackageData } from '@valk-nx/storyblok-services/src/lib/services/content.interface';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { ISbStoryBlok } from '@valk-nx/storyblok-types/src/lib/types/storyblok.types';

const Components = {
	carousel: SbCarouselComponent,
	section: SectionComponent,
};

@Component({
	selector: 'sb-package-overview',
	templateUrl: './package-overview.component.html',
	imports: [
		AlertComponent,
		CommonModule,
		DynamicModule,
		PersuadeCardComponent,
		SectionBgClass,
		SkeletonComponent,
		TranslatePipe,
	],
})
export class PackageOverviewComponent extends StoryblokRootDirective {
	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly router = inject(Router);
	private readonly rootLanguage = inject<Language>(LANGUAGE);

	@Input({ required: true }) content!: ISbStoryBlok[];

	// Needed to "mute" errors in console
	@Input({ required: true }) metaDescription!: string;
	@Input({ required: true }) metaTitle!: string;

	components: typeof Components = Components;
	currentUrl = '';
	packages$: Observable<PackageData[]>;
	alertType: AlertType = 'dialog--warning';

	pricingTooltipOccupancy = PRICING_TOOLTIP_OCCUPANCY;

	get contentInputs(): (InputsType & { component: string })[] {
		return this.content.map((blok) => ({
			...blok,
			component: blok['component'] ?? '',
		}));
	}

	constructor() {
		super();

		this.packages$ = this.sbService.translatedSlugs$.pipe(
			takeUntilDestroyed(),
			map((translatedSlugs) => {
				const { url } = this.router;
				this.currentUrl = removeTrailingSlash(removeParameters(url));

				const selectedSlug =
					translatedSlugs.find((x) => x.selected) ??
					translatedSlugs[0];
				const path =
					selectedSlug.lang.toLowerCase() !== this.rootLanguage
						? selectedSlug.path.substring(3)
						: selectedSlug.path;
				return {
					path,
					language: selectedSlug.lang.toLowerCase() as Language,
				};
			}),
			switchMap(({ path, language }) =>
				combineLatest([
					from(
						this.sbService.getStoriesByQuery(
							{
								starts_with: path,
								excluding_slugs: path,
								per_page: 100,
							},
							language,
						),
					),
					this.contentService.getPackages(language),
				]),
			),
			map(([storiesResponse, packagesResponse]) => {
				const storyblokPackages = storiesResponse.data.stories;
				const availablePackages = packagesResponse.data;

				return availablePackages.filter((availablePackage) =>
					storyblokPackages.some(
						(storyblokPackage) =>
							storyblokPackage.full_slug.split('/').pop() ===
							availablePackage.slug,
					),
				);
			}),
		);
	}
}
